<!-- Enhanced ProjectContext.vue -->
<template>
  <div class="project-context">
    <!-- Compact Project Banner -->
    <div 
      class="project-banner"
      :class="[urgencyData?.urgencyClass || 'banner-safe', { 'project-inactive': selectedProject?.status !== 'ACTIVE' }]"
    >
      <div class="banner-content">
        <!-- Project Icon -->
        <div class="project-icon">
          <i class="fas fa-briefcase"></i>
        </div>
        
        <!-- Main Project Info -->
        <div class="project-info">
          <div class="project-main">
            <!-- Company Name with skeleton -->
            <span v-if="clientInfo.company_name && clientInfo.company_name !== 'Your Company'" 
                  class="company-name">{{ clientInfo.company_name }}</span>
            <span v-else class="skeleton skeleton-company"></span>
            
            <!-- Project Name with skeleton -->
            <span v-if="selectedProject.project_name && selectedProject.project_name !== 'Loading...'" 
                  class="project-name">{{ selectedProject.project_name }}</span>
            <span v-else class="skeleton skeleton-project"></span>
            
            <!-- Status Badge -->
            <span v-if="selectedProject.status" 
                  class="project-status-badge" :class="selectedProject.status.toLowerCase()">
              {{ selectedProject.status }}
            </span>
          </div>
          
          <!-- Project Meta Info -->
          <div class="project-meta">
            <span v-if="selectedProject.job_title" class="job-title">{{ selectedProject.job_title }}</span>
            <span v-else class="skeleton skeleton-job"></span>
            
            <span v-if="projectDetails.industry" class="industry-info">
              <i class="fas fa-industry"></i> {{ projectDetails.industry }}
            </span>
            <span v-if="projectDetails.region" class="region-info">
              <i class="fas fa-globe"></i> {{ projectDetails.region }}
            </span>
          </div>
          
          <!-- Progress and Timeline -->
          <div class="project-progress">
            <div class="positions-info">
              <span class="positions-text">
                {{ selectedProject.positions_filled || 0 }}/{{ selectedProject.positions_required || 1 }} positions
              </span>
              <div class="progress-bar">
                <div class="progress-fill" :style="{ width: getProgressPercentage() + '%' }"></div>
              </div>
            </div>
            
            <div v-if="urgencyData.daysRemaining !== null" class="urgency-info">
              <span v-if="urgencyData.daysRemaining >= 0" class="days-remaining">
                {{ urgencyData.daysRemaining }}d left
              </span>
              <span v-else class="overdue-text">
                {{ Math.abs(urgencyData.daysRemaining) }}d overdue
              </span>
            </div>
          </div>
        </div>
        
        <!-- Agent Status Indicator -->
        <div class="agent-status">
          <div v-if="agentAssignment.checking" class="agent-status-item checking">
            <div class="status-icon">
              <i class="fas fa-spinner fa-spin"></i>
            </div>
            <span>Checking...</span>
          </div>
          <div v-else-if="agentAssignment.hasAgent" class="agent-status-item has-agent">
            <div class="status-icon">
              <i class="fas fa-user-check"></i>
            </div>
            <span>{{ agentAssignment.agentCount }} agent{{ agentAssignment.agentCount !== 1 ? 's' : '' }}</span>
          </div>
          <div v-else class="agent-status-item no-agent">
            <div class="status-icon">
              <i class="fas fa-user-clock"></i>
            </div>
            <span>Unassigned</span>
          </div>
        </div>
     
        <!-- Action Buttons -->
        <div class="project-actions">
          <button @click="toggleDetails" style="color:white !important" class="details-btn white-btn" :class="{ active: showDetails }">
            <i class="fas fa-info-circle"></i>
            Details
          </button>
          <button @click="$emit('switch-project')"  style="color:white !important" class="switch-btn white-btn">
            <i class="fas fa-exchange-alt"></i>
            Switch
          </button>
          <button @click="goToCandidatePool"  style="color:white !important"  class="pool-btn white-btn">
            <i class="fas fa-users"></i>
            Pool
          </button>
        </div>
      </div>
    </div>

    <!-- Expandable Details Section -->
    <div v-if="showDetails" class="details-section">
      <div class="details-grid">

        <!-- Job Info -->
        <div class="detail-card">
          <div class="card-header">
            <i class="fas fa-briefcase"></i>
            <span>Poste</span>
          </div>
          <div class="card-content">
            <div class="detail-item">
              <label>Intitulé :</label>
              <span>{{ projectDetails.job_title || selectedProject.job_title || '—' }}</span>
            </div>
            <div class="detail-item">
              <label>Postes :</label>
              <span>{{ projectDetails.positions_required || selectedProject.positions_required || 1 }}</span>
            </div>
          <div class="detail-item" v-if="projectDetails.additional_info?.seniority_level">
            <label>Séniorité :</label>
            <span>{{ projectDetails.additional_info.seniority_level }}</span>
          </div>
          <div class="detail-item" v-if="projectDetails.additional_info?.years_experience_min != null">
            <label>Expérience :</label>
            <span>{{ projectDetails.additional_info.years_experience_min }}–{{ projectDetails.additional_info.years_experience_max }} ans</span>
          </div>
                      
          </div>
        </div>
      
        <!-- Timeline -->
        <div class="detail-card">
          <div class="card-header">
            <i class="fas fa-calendar-alt"></i>
            <span>Timeline</span>
          </div>
          <div class="card-content">
            <div class="detail-item">
              <label>Début :</label>
              <span>{{ formatDate(projectDetails.start_date) }}</span>
            </div>
            <div class="detail-item">
              <label>Échéance :</label>
              <span :class="{ 'overdue-text': urgencyData.daysRemaining < 0 }">
                {{ formatDate(projectDetails.end_date) }}
              </span>
            </div>
            <div class="detail-item">
              <label>Durée :</label>
              <span>{{ calculateDuration() }} jours</span>
            </div>
          </div>
        </div>
      
        <!-- Company -->
        <div class="detail-card">
          <div class="card-header">
            <i class="fas fa-building"></i>
            <span>Entreprise</span>
          </div>
          <div class="card-content">
            <div class="detail-item">
              <label>Société :</label>
              <span>{{ clientInfo.company_name || '—' }}</span>
            </div>
            <div class="detail-item">
              <label>Secteur :</label>
              <span>{{ projectDetails.industry || 'Non spécifié' }}</span>
            </div>
            <div class="detail-item">
              <label>Région :</label>
              <span>{{ projectDetails.region || 'Non spécifié' }}</span>
            </div>
          </div>
        </div>
      
        <!-- Progress -->
        <div class="detail-card">
          <div class="card-header">
            <i class="fas fa-chart-line"></i>
            <span>Avancement</span>
          </div>
          <div class="card-content">
            <div class="detail-item">
              <label>Completion :</label>
              <span class="progress-percentage">{{ getProgressPercentage() }}%</span>
            </div>
            <div class="detail-item">
              <label>Restant :</label>
              <span>{{ (selectedProject.positions_required || 1) - (selectedProject.positions_filled || 0) }} poste(s)</span>
            </div>
          </div>
        </div>
      
      </div>
      
      <!-- Job Description -->
      <div v-if="projectDetails.job_description" class="job-description-card" style="margin-bottom: 0.75rem;">
        <div class="card-header">
          <i class="fas fa-file-alt"></i>
          <span>Description du poste</span>
          <button @click="toggleJobDescription" class="expand-btn">
            <i :class="showFullJobDescription ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
          </button>
        </div>
        <div class="job-description-content" :class="{ expanded: showFullJobDescription }" style="padding: 0.75rem;">
          <p style="white-space: pre-wrap;">{{ projectDetails.job_description }}</p>
        </div>
      </div>
      
      <!-- Project Links -->
      <div v-if="projectDetails.project_links?.length" class="job-description-card">
        <div class="card-header">
          <i class="fas fa-paperclip"></i>
          <span>Documents & Liens</span>
        </div>
        <div style="padding: 0.75rem; display: flex; flex-direction: column; gap: 0.5rem;">
         <a
            v-for="link in projectDetails.project_links"
            :key="link.url"
            :href="link.url"
            target="_blank"
            rel="noopener noreferrer"
            class="project-link-item"
          >
            <i class="fas fa-external-link-alt"></i>
            {{ link.label || link.url }}
          </a>
        </div>
      </div>

      <!-- Job Description Section -->
      <div v-if="projectDetails.additional_info?.additional_context" class="job-description-card">
        <div class="card-header">
          <i class="fas fa-file-alt"></i>
          <span>Contexte additionnel</span>
          <button @click="toggleJobDescription" class="expand-btn">
            <i :class="showFullJobDescription ? 'fas fa-chevron-up' : 'fas fa-chevron-down'"></i>
          </button>
        </div>
        <div class="job-description-content" :class="{ expanded: showFullJobDescription }">
          <p>{{ projectDetails.additional_info.additional_context }}</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';

export default {
  name: 'ProjectContext',
  props: {
    selectedProject: {
      type: Object,
      required: true
    }
  },
  
  emits: ['view-details', 'switch-project'],
  
  data() {
    return {
      refreshTimer: null,

      showDetails: false,
      showFullJobDescription: false,
      agentAssignment: {
        hasAgent: null,
        agentCount: 0,
        checking: true
      },
      clientInfo: {
        project_name: null,
        job_title: null,
        job_description: null,
        company_name: null
      },
      projectDetails: {
        industry: null,
        region: null,
        tier_name: null,
        base_price_usd: null,
        start_date: null,
        end_date: null
      }
    };
  },
  
watch: {
  // Watch for changes in selectedProject prop
  selectedProject: {
    handler(newProject, oldProject) {
      if (newProject && oldProject && 
          newProject.project_id === oldProject.project_id &&
          (newProject.positions_filled !== oldProject.positions_filled ||
           newProject.status !== oldProject.status)) {
        console.log('🔄 ProjectContext: Project data updated', newProject);
        // Force reactivity update
        this.$forceUpdate();
      }
    },
    deep: true,
    immediate: false
  }
},
  async mounted() {
    await this.loadProjectData();

// Simple refresh every 3 seconds to catch project updates
  // Refresh every 2 seconds when project is selected
  this.refreshTimer = setInterval(() => {
    if (this.selectedProject?.project_id) {
      this.checkProjectUpdates();
    }
  }, 20000);
},

beforeUnmount() {
  if (this.refreshTimer) {
    clearInterval(this.refreshTimer);
  }
},

  computed: {
    urgencyData() {
      if (!this.projectDetails?.end_date) {
        return { urgencyLevel: 'safe', urgencyClass: 'banner-safe', daysRemaining: null };
      }
      
      const today = new Date();
      const endDate = new Date(this.projectDetails.end_date);
      const timeDiff = endDate.getTime() - today.getTime();
      const daysRemaining = Math.ceil(timeDiff / (1000 * 3600 * 24));
      
      let urgencyLevel, urgencyClass;
      
      if (daysRemaining < 0) {
        urgencyLevel = 'overdue';
        urgencyClass = 'banner-overdue';
      } else if (daysRemaining <= 7) {
        urgencyLevel = 'critical';
        urgencyClass = 'banner-critical';
      } else if (daysRemaining <= 20) {
        urgencyLevel = 'warning';
        urgencyClass = 'banner-warning';  
      } else if (daysRemaining <= 30) {
        urgencyLevel = 'caution';
        urgencyClass = 'banner-caution';
      } else {
        urgencyLevel = 'safe';
        urgencyClass = 'banner-safe';
      }
      
      return { urgencyLevel, urgencyClass, daysRemaining };
    }
  },
  
  methods: {
    goToCandidatePool() {
      // Navigate to candidate pool page
      this.$router.push({
        name: 'CandidatePool', 
        params: {
          projectId: this.selectedProject.project_id
        }
      });
    },
    // refresh this method:
    async checkProjectUpdates() {
  try {
    const response = await axios.get(`/api/v1/clients/projects/${this.selectedProject.project_id}`);
    const updated = response.data;
    
    if (updated.positions_filled !== this.selectedProject.positions_filled ||
        updated.status !== this.selectedProject.status) {
      console.log('🔄 Project updated:', updated.positions_filled + '/' + updated.positions_required);
      this.selectedProject.positions_filled = updated.positions_filled;
      this.selectedProject.status = updated.status;
      this.$forceUpdate();
    }
  } catch (error) {
    // Silent fail
  }
},
   
    // FIX: Handle project updates without full reload
  // handleProjectUpdate(updateData) {
  //   if (updateData.project_id === this.selectedProject.project_id) {
  //     console.log('🔄 ProjectContext: Updating project data', updateData);
      
  //     // Update positions without flicker
  //     this.selectedProject.positions_filled = updateData.positions_filled;
  //     this.selectedProject.positions_required = updateData.positions_required;
  //     this.selectedProject.status = updateData.status;
      
  //     // Force reactivity update
  //     this.$forceUpdate();
  //   }
  // },
    async loadProjectData() {
      if (!this.selectedProject?.project_id) return;
      
      try {
        console.log('🔍 ProjectContext: Loading comprehensive project data...');
        
        // Load both agent assignment and detailed project info
        const [agentResponse, detailsResponse] = await Promise.all([
          axios.get(`/api/v1/projects/${this.selectedProject.project_id}/agent-assignment`),
          axios.get(`/api/v1/projects/${this.selectedProject.project_id}/details`)
        ]);

        // Agent assignment data
        this.agentAssignment = {
          hasAgent: agentResponse.data.has_agent,
          agentCount: agentResponse.data.agent_count,
          checking: false
        };

        // Complete project details 
        const details = detailsResponse.data;
        this.clientInfo = {
          company_name: details.company_name,
          project_name: details.project_name,
          job_title: details.job_title,
          job_description: details.job_description
        };

       // all details
      this.projectDetails = {
        industry: details.industry,
        region: details.region,
        tier_name: details.tier_name,
        base_price_usd: details.base_price_usd,
        start_date: details.start_date,
        end_date: details.end_date,
        job_description: details.job_description,
        job_title: details.job_title,
        project_name: details.project_name,
        positions_required: details.positions_required,
        positions_filled: details.positions_filled,
        project_links: details.project_links || [],
        additional_info: details.additional_info || null,

      };

        console.log('✅ ProjectContext: Complete project data loaded:', this.projectDetails);
        
      } catch (error) {
        console.error('❌ ProjectContext: Error loading project data:', error);
        
        // Set safe defaults
        this.agentAssignment = { hasAgent: true, agentCount: 0, checking: false };
        this.clientInfo = { company_name: null, project_name: null, job_title: null, job_description: null };
        this.projectDetails = {  industry: null, region: null, tier_name: null, base_price_usd: null, 
            start_date: null, end_date: null, job_description: null, 
            job_title: null, project_name: null, project_links: []  };
      }
    },

    toggleDetails() {
      this.showDetails = !this.showDetails;
    },

    toggleJobDescription() {
      this.showFullJobDescription = !this.showFullJobDescription;
    },

    getProgressPercentage() {
      if (!this.selectedProject.positions_required) return 0;
      return Math.min(100, ((this.selectedProject.positions_filled || 0) / this.selectedProject.positions_required) * 100);
    },
    
    formatDate(dateStr) {
      if (!dateStr) return 'Not set';
      return new Date(dateStr).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    calculateDuration() {
      if (!this.projectDetails.start_date || !this.projectDetails.end_date) return 'N/A';
      
      const start = new Date(this.projectDetails.start_date);
      const end = new Date(this.projectDetails.end_date);
      const diffTime = Math.abs(end - start);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      
      return diffDays;
    }
  }
};
</script>

<style scoped>
.project-banner .banner-content,
.project-banner .banner-content *:not(button) {
  color: white !important;
}

/* Ensure all text elements stay white on colored backgrounds */
.company-name,
.project-name,
.project-status-badge,
.job-title,
.industry-info,
.region-info,
.positions-text,
.days-remaining,
.overdue-text,
.agent-status-item,
.project-icon i {
  color: white !important;
}
.project-context {
  margin-bottom: 0rem;
}

/* COMPACT BANNER STYLES */
.project-banner {
  position: relative;
  border-radius: 0.75rem;
  overflow: hidden;
  transition: all 0.3s ease;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  border-left: 4px solid transparent;
}

/* Urgency Color Schemes */
.banner-safe { background: linear-gradient(135deg, #10b981 0%, #059669 100%); border-left-color: #065f46; }
.banner-caution { background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%); border-left-color: #92400e; }
.banner-warning { background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%); border-left-color: #991b1b; }
.banner-critical { background: linear-gradient(135deg, #dc2626 0%, #b91c1c 100%); border-left-color: #7f1d1d; }
.banner-overdue { background: linear-gradient(135deg, #b91c1c 0%, #7f1d1d 100%); border-left-color: #450a0a; animation: pulse-urgent 2s infinite; }
.project-inactive { background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%) !important; border-left-color: #374151 !important; opacity: 0.7; }

/* COMPACT BANNER CONTENT */
.banner-content {
  padding: 1rem; /* Reduced from 1.5rem */
  display: flex;
  align-items: center;
  gap: 1rem; /* Reduced from 1.5rem */
  color: white;
  position: relative;
  z-index: 2;
}

.project-icon {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5rem; /* Reduced from 3rem */
  height: 2.5rem;
  background: rgba(255, 255, 255, 0.15);
  border-radius: 50%;
  backdrop-filter: blur(4px);
}

.project-icon i {
  font-size: 1rem; /* Reduced from 1.25rem */
  color: white;
}

.project-info {
  flex: 1;
  min-width: 0;
  line-height: 1.3;
}

.project-main {
  display: flex;
  align-items: center;
  gap: 0.5rem; /* Reduced */
  margin-bottom: 0.25rem; /* Reduced */
  flex-wrap: wrap;
}

/* COMPACT TEXT SIZES */
.company-name, .client-name {
  font-size: 1rem; /* Reduced from 1.125rem */
  font-weight: 600; /* Reduced from 700 */
  color: white;
  text-shadow: 0 1px 2px rgba(0, 0, 0, 0.1);
}

.project-name {
  font-size: 0.9rem; /* Reduced from 1rem */
  font-weight: 500;
  color: rgba(255, 255, 255, 0.9);
}

.project-name::before {
  content: "•";
  margin-right: 0.5rem;
  color: rgba(255, 255, 255, 0.6);
}

.project-status-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.125rem 0.375rem;
  border-radius: 0.5rem;
  font-size: 0.7rem; /* Reduced */
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.025em;
}

.project-meta {
  display: flex;
  align-items: center;
  gap: 0.75rem; /* Reduced */
  font-size: 0.8rem; /* Reduced */
  color: rgba(255, 255, 255, 0.8);
  margin-bottom: 0.5rem;
  flex-wrap: wrap;
}

.job-title {
  font-style: italic;
}

.industry-info, .region-info {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem;
  opacity: 0.9;
}

.project-progress {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
}

.positions-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.positions-text {
  font-size: 0.8rem; /* Reduced */
  color: rgba(255, 255, 255, 0.9);
  font-weight: 500;
}

.progress-bar {
  width: 100px; /* Reduced from 120px */
  height: 5px; /* Reduced from 6px */
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 3px;
  transition: width 0.3s ease;
}

.urgency-info {
  font-size: 0.8rem; /* Reduced */
  font-weight: 600;
}

.days-remaining { color: rgba(255, 255, 255, 0.9); }
.overdue-text { color: #fef2f2; animation: pulse-text 1.5s infinite; }

/* COMPACT AGENT STATUS */
.agent-status {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  margin-right: 0.5rem;
}

.agent-status-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.25rem;
  font-size: 0.75rem; /* Reduced */
  text-align: center;
}

.status-icon {
  width: 1.5rem; /* Reduced */
  height: 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
}

.agent-status-item.has-agent .status-icon { color: #4ade80; }
.agent-status-item.no-agent .status-icon { color: #fbbf24; }
.agent-status-item.checking .status-icon { color: #94a3b8; }

/* COMPACT ACTION BUTTONS */
.project-actions {
  display: flex;
  color: white !important; /* Only in banner context */
  flex-direction: column;
  gap: 0.375rem; /* Reduced */
  flex-shrink: 0;
}
.white-btn{
  color:white !important;
}
.details-btn, .switch-btn , .pool-btn{
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.25rem;
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: 1px solid rgba(255, 255, 255, 0.3);
  padding: 0.375rem 0.75rem; /* Reduced */
  border-radius: 0.375rem;
  font-size: 0.75rem; /* Reduced */
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  backdrop-filter: blur(4px);
  white-space: nowrap;
}

.details-btn:hover, .switch-btn:hover , .pool-btn:hover {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

.details-btn.active {
  background: rgba(255, 255, 255, 0.3);
  border-color: rgba(255, 255, 255, 0.5);
}

/* EXPANDABLE DETAILS SECTION */
.details-section {
  background: var(--surface-color);
  border: 2px solid var(--border-color);
  border-radius: 0.75rem;
  padding: 1rem;
  margin-top: 0.75rem;
  backdrop-filter: blur(4px);
}

/* Light theme - subtle pink tint */
:root[data-theme="light"] .details-section {
  background: #fff5f5;
  border-color: #fca5a5;
}

/* Dark theme - keep dark */
:root[data-theme="dark"] .details-section {
  background: rgba(0, 0, 0, 0.3);
  border-color: rgba(255, 255, 255, 0.1);
}

.details-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
}

.card-content {
  display: flex;
  flex-direction: column;
  gap: 0.375rem; /* Reduced */
}

/*  .detail-card and related rules  : */

.detail-card {
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  padding: 0.75rem;
  color: var(--text-color);
}

:root[data-theme="light"] .detail-card {
  background: white;
  border-color: #fca5a5;
}

:root[data-theme="dark"] .detail-card {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

.card-header {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-color);
}

.card-header i {
  color: var(--primary-color);
  font-size: 0.75rem;
}

.detail-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 0.75rem;
  color: var(--text-color);
}

.detail-item label {
  color: var(--text-color);
  opacity: 0.7;
  font-weight: 500;
}

.detail-item span {
  color: var(--text-color);
  font-weight: 500;
}

.tier-badge {
  background: rgba(255, 255, 255, 0.2);
  padding: 0.125rem 0.375rem;
  border-radius: 0.375rem;
  font-size: 0.7rem;
  color: white !important; /* Only in banner context */
  font-weight: 600;
}


/* But make them theme-aware in details section */
.detail-card .tier-badge {
  background: var(--surface-color);
  color: var(--text-color) !important;
  border: 1px solid var(--border-color);
}

:root[data-theme="light"] .detail-card .tier-badge {
  background: #fff5f5;
  border-color: #fca5a5;
  color: #991b1b !important;
}

.budget-amount,
.progress-percentage {
  font-weight: 600;
}

/* Keep green for budget in both themes */
.budget-amount {
  color: #4ade80 !important;
}

/* Keep blue for progress in both themes */
.progress-percentage {
  color: #60a5fa !important;
}
.budget-amount {
  color: #4ade80;
  font-weight: 600;
}

.progress-percentage {
  color: #60a5fa;
  font-weight: 600;
}

/* JOB DESCRIPTION CARD */

.job-description-card {
  background: var(--surface-color);
  border: 1px solid var(--border-color);
  border-radius: 0.5rem;
  color: var(--text-color);
  overflow: hidden;
}

:root[data-theme="light"] .job-description-card {
  background: white;
  border-color: #fca5a5;
}

:root[data-theme="dark"] .job-description-card {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.1);
}

.job-description-card .card-header {
  padding: 0.75rem;
  background: var(--surface-color);
  border-bottom: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  color: var(--text-color);
}

:root[data-theme="light"] .job-description-card .card-header {
  background: #fff5f5;
  border-bottom-color: #fca5a5;
}

:root[data-theme="dark"] .job-description-card .card-header {
  background: rgba(255, 255, 255, 0.05);
  border-bottom-color: rgba(255, 255, 255, 0.1);
}

.expand-btn {
  background: none;
  border: none;
  color: var(--text-color);
  opacity: 0.7;
  cursor: pointer;
  padding: 0.25rem;
  border-radius: 0.25rem;
  transition: all 0.2s ease;
}

.expand-btn:hover {
  background: var(--hover-color);
  opacity: 1;
}

.job-description-content p {
  margin: 0;
  font-size: 0.8rem;
  line-height: 1.4;
  color: var(--text-color);
}

/* LOADING SKELETONS */
.skeleton {
  background: linear-gradient(90deg, 
    rgba(255, 255, 255, 0.1) 25%, 
    rgba(255, 255, 255, 0.2) 50%, 
    rgba(255, 255, 255, 0.1) 75%
  );
  background-size: 200px 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 0.25rem;
  display: inline-block;
}

.skeleton-company { width: 120px; height: 1rem; }
.skeleton-project { width: 100px; height: 0.9rem; }
.skeleton-job { width: 140px; height: 0.8rem; }

@keyframes skeleton-loading {
  0% { background-position: -200px 0; }
  100% { background-position: calc(200px + 100%) 0; }
}

@keyframes pulse-urgent {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

@keyframes pulse-text {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.7; }
}

/* MOBILE RESPONSIVENESS */
@media (max-width: 768px) {
  .banner-content {
    padding: 0.75rem;
    gap: 0.75rem;
    flex-direction: column;
    text-align: center;
  }
  
  .project-icon {
    width: 2rem;
    height: 2rem;
  }
  
  .project-main {
    flex-direction: column;
    gap: 0.25rem;
    align-items: center;
  }
  
  .project-name::before {
    display: none;
  }
  
  .project-meta {
    justify-content: center;
    gap: 0.5rem;
  }
  
  .project-actions {
    flex-direction: row;
    gap: 0.5rem;
  }
  
  .details-grid {
    grid-template-columns: 1fr;
  }
}

.project-link-item {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.8rem;
  font-weight: 500;
  color: #60a5fa;
  text-decoration: none;
  padding: 0.3rem 0.6rem;
  border-radius: 0.375rem;
  border: 1px solid rgba(96, 165, 250, 0.3);
  background: rgba(96, 165, 250, 0.08);
  transition: all 0.2s ease;
  width: fit-content;
}
.project-link-item:hover {
  background: rgba(96, 165, 250, 0.18);
  border-color: rgba(96, 165, 250, 0.6);
  color: #93c5fd;
}

:root[data-theme="light"] .project-link-item {
  color: #2563eb;
  border-color: rgba(37, 99, 235, 0.3);
  background: rgba(37, 99, 235, 0.06);
}
:root[data-theme="light"] .project-link-item:hover {
  background: rgba(37, 99, 235, 0.12);
}

.job-description-content {
  max-height: 80px;
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding: 0 0.75rem;
}
.job-description-content.expanded {
  max-height: 500px;
}
</style>